import React from "react"
import {
	Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material"
import { dsColorNew } from '@palette'
import { IconSeeker } from "@ui-seeker/core"
import { AppBulkEditAction, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader } from "@components"
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { weekdaysLabel } from "Pages/DsRepricing/Page/@components/Weekdays"
import { readPeriod } from "Pages/DsRepricing/Page/@components/periodShape"

const UNDERCUT_TYPE_LABEL = { 0: '€', 1: '%' }
const UNDERCUT_DIRECTION_LABEL = { 0: 'Down', 1: 'Up' }
// The arrow beside the word, as an icon rather than a ▼/▲ glyph in the text
const UNDERCUT_DIRECTION_ICON = { 0: 'arrow-down', 1: 'arrow-up' }

// ─── item formatters ───────────────────────────────────────────────────────────

const fmtSeason = s => `${s.seasonFrom} → ${s.seasonTo}`

const fmtDuration = d => `${d.durationFrom} → ${d.durationTo}`

const fmtLeadTime = w => `${w.leadFrom} → ${w.leadTo} days`

const fmtFilterItem = item => (typeof item === 'object' && item !== null) ? item.label : String(item)

const STRATEGY_LABEL = { market_trend: 'Market Trend', ranking: 'Ranking' }

// The face's own words (`PREPAID_OPTIONS`), and "All" for an unscoped axis.
const TARIFF_TYPE_LABEL = { '': 'All tariff types', '1': 'Prepaid', '0': 'Non prepaid' }

const fmtStrategy = s => {
	if (!s?.type) return '-'
	const base = STRATEGY_LABEL[s.type] ?? s.type
	if (s.type === 'ranking') return `${base} · position ${s.targetPosition ?? '-'}`
	const extras = [
		s.rankCap != null ? `rank cap ${s.rankCap}` : null,
		s.excludeOutliersPct != null ? `exclude >${s.excludeOutliersPct}%` : null,
	].filter(Boolean)
	return extras.length ? `${base} · ${extras.join(', ')}` : base
}

// ─── diff helpers ──────────────────────────────────────────────────────────────

// Plain JSON.stringify is sensitive to key order, and the backend does not promise the same key
// order the front reconstructs on save (e.g. `minMax` rows come back as
// `{ acriss, durationFrom, durationTo, max, min }` but are rebuilt as `{ ..., min, max }`) — with
// the raw stringify, that alone flags every untouched row as "changed". Sorting keys recursively
// makes the comparison depend only on the actual values.
const stableStringify = v => {
	if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`
	if (v !== null && typeof v === 'object') {
		return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${stableStringify(v[k])}`).join(',')}}`
	}
	return JSON.stringify(v)
}

const serialize = v => stableStringify(v ?? null)

function scalarDiff(label, oldVal, newVal) {
	if (serialize(oldVal) === serialize(newVal)) return null
	return { label, type: 'scalar', oldVal: String(oldVal ?? '-'), newVal: String(newVal ?? '-') }
}

const withIcons = (row, oldIcon, newIcon) => row && { ...row, oldIcon, newIcon }

function arrayDiff(label, oldArr, newArr, keyFn, labelFn) {
	const old_ = oldArr ?? []
	const new_ = newArr ?? []
	if (serialize(old_) === serialize(new_)) return null

	const oldKeys = old_.map(keyFn)
	const newKeys = new_.map(keyFn)

	const removed = old_.filter(i => !newKeys.includes(keyFn(i))).map(labelFn)
	const added = new_.filter(i => !oldKeys.includes(keyFn(i))).map(labelFn)
	const changed = new_.filter(i => {
		if (!oldKeys.includes(keyFn(i))) return false
		const orig = old_.find(o => keyFn(o) === keyFn(i))
		return serialize(orig) !== serialize(i)
	}).map(i => {
		const orig = old_.find(o => keyFn(o) === keyFn(i))
		return { before: labelFn(orig), after: labelFn(i) }
	})

	return { label, type: 'array', added, removed, changed }
}

function buildDiff(original, current) {
	const rows = []
	const push = d => { if (d) rows.push(d) }

	push(scalarDiff('Name', original.name, current.name))
	push(scalarDiff('Priority', original.priority ?? '-', current.priority ?? '-'))
	push(scalarDiff('Strategy', fmtStrategy(original.strategy), fmtStrategy(current.strategy)))
	push(scalarDiff('Undercut value', original.undercut?.value, current.undercut?.value))
	push(scalarDiff('Undercut type', UNDERCUT_TYPE_LABEL[original.undercut?.type], UNDERCUT_TYPE_LABEL[current.undercut?.type]))
	push(withIcons(
		scalarDiff('Undercut direction', UNDERCUT_DIRECTION_LABEL[original.undercut?.direction ?? 0], UNDERCUT_DIRECTION_LABEL[current.undercut?.direction ?? 0]),
		UNDERCUT_DIRECTION_ICON[original.undercut?.direction ?? 0], UNDERCUT_DIRECTION_ICON[current.undercut?.direction ?? 0],
	))
	push(arrayDiff('Seasons',
		readPeriod(original).seasons, readPeriod(current).seasons,
		s => `${s.seasonFrom}|${s.seasonTo}`, fmtSeason
	))
	push(arrayDiff('Durations',
		original.durations, current.durations,
		d => `${d.durationFrom}|${d.durationTo}`, fmtDuration
	))
	push(arrayDiff('Reports',
		original.reports, current.reports,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(arrayDiff('Offices filter',
		original.filterGroupOffices, current.filterGroupOffices,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Offices filter mode',
		original.filterGroupOfficesExclusion ? 'Exclude' : 'Include',
		current.filterGroupOfficesExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Sites filter',
		original.filterGroupSites, current.filterGroupSites,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Sites filter mode',
		original.filterGroupSitesExclusion ? 'Exclude' : 'Include',
		current.filterGroupSitesExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Providers filter',
		original.filterGroupProviders, current.filterGroupProviders,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Providers filter mode',
		original.filterGroupProvidersExclusion ? 'Exclude' : 'Include',
		current.filterGroupProvidersExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Web Supplier Groups filter',
		original.filterGroupWebSuppliers, current.filterGroupWebSuppliers,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Web Supplier Groups filter mode',
		original.filterGroupWebSuppliersExclusion ? 'Exclude' : 'Include',
		current.filterGroupWebSuppliersExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Acriss filter',
		original.filterAcriss, current.filterAcriss,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Acriss filter mode',
		original.filterAcrissExclusion ? 'Exclude' : 'Include',
		current.filterAcrissExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Categories filter',
		original.filterCategories, current.filterCategories,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Categories filter mode',
		original.filterCategoriesExclusion ? 'Exclude' : 'Include',
		current.filterCategoriesExclusion ? 'Exclude' : 'Include'
	))
	// The face axes (CU-869fe6mf4). The tariff type is a list of one code
	// and reads by its name; both or none is "All".
	push(scalarDiff('Tariff type',
		TARIFF_TYPE_LABEL[original.filterTariffTypes?.length === 1 ? String(original.filterTariffTypes[0]) : ''],
		TARIFF_TYPE_LABEL[current.filterTariffTypes?.length === 1 ? String(current.filterTariffTypes[0]) : '']
	))
	push(arrayDiff('Insurance filter',
		original.filterInsurances, current.filterInsurances,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Insurance filter mode',
		original.filterInsurancesExclusion ? 'Exclude' : 'Include',
		current.filterInsurancesExclusion ? 'Exclude' : 'Include'
	))
	// Who the rule is compared against (CU-869fh646w): without these two
	// rows, switching one on and saving confirmed a diff that said
	// nothing changed.
	push(scalarDiff('Compare against every tariff type',
		original.compareEveryTariffType ? 'On' : 'Off',
		current.compareEveryTariffType ? 'On' : 'Off'
	))
	push(scalarDiff('Compare against every insurance',
		original.compareEveryInsurance ? 'On' : 'Off',
		current.compareEveryInsurance ? 'On' : 'Off'
	))
	push(scalarDiff('Compare By',
		original.compareByCategory ? 'Category' : 'ACRISS',
		current.compareByCategory ? 'Category' : 'ACRISS'
	))
	push(arrayDiff('Competitor rule — never cheaper than',
		original.competitorRule?.neverBelow, current.competitorRule?.neverBelow,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(arrayDiff('Competitor rule — never dearer than',
		original.competitorRule?.neverAbove, current.competitorRule?.neverAbove,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(arrayDiff('Lead time windows',
		readPeriod(original).leadTimes, readPeriod(current).leadTimes,
		w => `${w.leadFrom}|${w.leadTo}`, fmtLeadTime
	))
	push(scalarDiff('Weekdays',
		weekdaysLabel(readPeriod(original).weekdays) || '-',
		weekdaysLabel(readPeriod(current).weekdays) || '-'
	))
	push(scalarDiff('Smart ACRISS pricing',
		original.tools?.smartAcrissPricing ? 'On' : 'Off',
		current.tools?.smartAcrissPricing ? 'On' : 'Off'
	))
	// The Max Mark-Up, Tethering, Smart-ACRISS/Category-groups and Min/Max
	// diffs used to live here — those fields moved to Setup (Rate Cards,
	// Tethering, Category Consistency, Price Limits) and are no longer
	// owned by the strategy. Their diffs live in Setup's own history if
	// needed.

	return rows
}

// ─── sub-components ────────────────────────────────────────────────────────────

function ScalarRow({ label, oldVal, newVal, oldIcon, newIcon }) {
	return (
		<TableRow>
			<TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>{label}</TableCell>
			<TableCell sx={{ verticalAlign: 'top' }}>
				<Chip label={oldVal} size="small" variant="outlined"
					icon={oldIcon ? <IconSeeker icon={oldIcon} sx={{ fontSize: 11 }}/> : undefined}
					sx={{ maxWidth: 160, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}/>
			</TableCell>
			<TableCell sx={{ verticalAlign: 'top' }}>
				<Chip label={newVal} size="small" color="primary"
					icon={newIcon ? <IconSeeker icon={newIcon} sx={{ fontSize: 11 }}/> : undefined}
					sx={{ maxWidth: 160, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}/>
			</TableCell>
		</TableRow>
	)
}

function ArrayRow({ label, added, removed, changed }) {
	return (
		<TableRow>
			<TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>{label}</TableCell>
			<TableCell colSpan={2}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
					{removed.map((item, i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<IconSeeker icon="minus" fontSize="small" sx={{ color: dsColorNew.dangerRed, fontSize: 12 }}/>
							<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.dangerRed }}>{item}</Typography>
						</Box>
					))}
					{added.map((item, i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<IconSeeker icon="plus" fontSize="small" sx={{ color: dsColorNew.successText, fontSize: 12 }}/>
							<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.successText }}>{item}</Typography>
						</Box>
					))}
					{changed.map((item, i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
							<IconSeeker icon="pen" fontSize="small" sx={{ color: dsColorNew.warningBar, fontSize: 12 }}/>
							<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted, textDecoration: 'line-through' }}>{item.before}</Typography>
							<IconSeeker icon="arrow-right" fontSize="small" sx={{ fontSize: 10 }}/>
							<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.warningBar }}>{item.after}</Typography>
						</Box>
					))}
				</Box>
			</TableCell>
		</TableRow>
	)
}

// ─── main component ────────────────────────────────────────────────────────────

export default function ConfirmEditModal({ open, onClose, onConfirm, original, current, isLoading }) {
	if (!original || !current) return null

	const diff = buildDiff(original, current)

	return (
		<AppDialog open={open} onClose={onClose} maxWidth="sm">
			<AppDialogHeader icon="floppy-disk" title="Confirm changes" onClose={onClose}/>
			<AppDialogBody sx={dialogBodySx}>
				{diff.length === 0 ? (
					<Typography sx={{ fontSize: 14, color: dsColorNew.textMuted }}>No changes detected.</Typography>
				) : (
					<>
						<Typography sx={{ fontSize: 14, color: dsColorNew.textMuted, mb: 1.5 }}>
							The following fields will be updated:
						</Typography>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ width: '30%' }}>Field</TableCell>
									<TableCell>Before</TableCell>
									<TableCell>After</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{diff.map(row => row.type === 'scalar'
									? <ScalarRow key={row.label} {...row}/>
									: <ArrayRow key={row.label} {...row}/>
								)}
							</TableBody>
						</Table>
					</>
				)}
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}/>
				<AppBulkEditAction
					loading={isLoading}
					startIcon={<IconSeeker icon="floppy-disk"/>}
					onClick={onConfirm}
					disabled={diff.length === 0}
				>
					Confirm
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}